import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import axios from "axios";
import qs from "qs";
import centralApiAxios from "../../axios/centralApiAxios";
import {
  LOGIN_USER,
  REGISTER_USER,
  LOGOUT_USER,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  AUTO_LOGIN,
  GET_USER_INFO,
} from "../actions";

import {
  loginUserSuccess,
  loginUserError,
  registerUserSuccess,
  registerUserError,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPasswordSuccess,
  resetPasswordError,
  getUserInfoSuccess,
  getUserInfoError,
} from "./actions";
import getClientId from "../../hooks/auth/clientIdHook";

const AUTO_SIGN_IN_ERROR = "Tenant created, but auto login fails.";
const API_LOGIN_ERROR = "In-correct user name or password";

const LoginWithEmailPasswordAsync = async (email, password) => {
  const clientId = getClientId();
  if (email.length > 0 && password.length > 0) {
    const axiosConfig = {
      baseURL: process.env.REACT_APP_IDN_BASE_URL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };

    const requestData = {
      client_id: clientId,
      client_secret: process.env.REACT_APP_API_CLIENT_SECRET,
      grant_type: "password",
      username: email,
      password,
    };

    try {
      const responseObject = await axios.post(
        "/connect/token",
        qs.stringify(requestData),
        axiosConfig
      );
      if (responseObject.data && responseObject.data.access_token) {
        return {
          user: {
            uid: email,
          },
          message: null,
          response: responseObject.data,
        };
      }
    } catch (err) {
      return {
        message: API_LOGIN_ERROR,
      };
    }
  }

  return {
    message: API_LOGIN_ERROR,
  };
};

const getUserInfoAsync = async (userId, parentActivity) => {
  try {
    const userInfoResponseObject = await centralApiAxios.get(
      `/Contacts/user?user_name=&user_id=${userId}`
    );
    if (userInfoResponseObject.data) {
      return userInfoResponseObject.data;
    }
    return { message: "Get user info failed." };
  } catch (error) {
    console.log(`Get user info failed.${error}`);
    return { message: "Get user info failed." };
  }
};

function* getUserInfo({ payload }) {
  try {
    const loginUser = yield call(getUserInfoAsync, payload.userId);
    if (!loginUser.message) {
      localStorage.setItem("user_id", payload.userName);
      localStorage.setItem("user_contactId", loginUser.contactId);
      localStorage.setItem("user_firstName", loginUser.firstName);
      localStorage.setItem("user_lastName", loginUser.lastName);
      localStorage.setItem("user_displayName", loginUser.displayName);
      localStorage.setItem("user_primaryEmail", loginUser.primaryEmail);
      localStorage.setItem("user_userAccountType", loginUser.userAccountType ? loginUser.userAccountType : "");
      yield put(getUserInfoSuccess(loginUser));
    } else {
      yield put(loginUserError(loginUser.message));
    }
  } catch (error) {
    console.log(error);
    yield put(getUserInfoError(error.message));
  }
}

function* loginWithEmailPassword({ payload }) {
  const { email, password } = payload.user;
  const { history } = payload;
  try {
    const loginUser = yield call(LoginWithEmailPasswordAsync, email, password);
    if (!loginUser.message) {
      localStorage.setItem("user_id", loginUser.user.uid);
      localStorage.setItem("access_token", loginUser.response.access_token);
      localStorage.setItem("refresh_token", loginUser.response.refresh_token);
      localStorage.setItem(
        "access_token_expiry",
        Date.now() + loginUser.response.expires_in * 1000
      );
      const userInfo = yield call(getUserInfoAsync, loginUser.user.uid);
      if (!userInfo.message) {
        localStorage.setItem("user_contactId", userInfo.contactId);
        localStorage.setItem("user_firstName", userInfo.firstName);
        localStorage.setItem("user_lastName", userInfo.lastName);
        localStorage.setItem("user_displayName", userInfo.displayName);
        localStorage.setItem("user_primaryEmail", userInfo.primaryEmail);
      }
      yield put(loginUserSuccess(loginUser.user));
      history.push("/");
    } else {
      yield put(loginUserError(loginUser.message));
    }
  } catch (error) {
    yield put(loginUserError(error));
  }
}

const AutoSigninAsync = async (otac) => {
  const axiosConfig = {
    baseURL: process.env.REACT_APP_IDN_BASE_URL,
    timeout: 60000,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  };

  const requestData = {
    clientId: getClientId(),
    clientSecret: process.env.REACT_APP_API_CLIENT_SECRET,
    apiResourceName: "CentralAPI",
    apiResourceDisplayName: "Central API",
    otac,
  };

  try {
    const responseObject = await axios.post(
      "/IdentityTools/AutoLogin",
      qs.stringify(requestData),
      axiosConfig
    );
    if (responseObject.data && responseObject.data.access_token) {
      return {
        message: null,
        response: responseObject.data,
      };
    }
    return {
      message: AUTO_SIGN_IN_ERROR,
    };
  } catch (err) {
    return {
      message: AUTO_SIGN_IN_ERROR,
    };
  }
};

function* AutoLogin({ payload }) {
  const clientId = getClientId();
  const { otac, email, cid, firstName, lastName, history } = payload;
  try {
    const loginUser = yield call(AutoSigninAsync, otac);
    if (!loginUser.message) {
      sessionStorage.setItem(
        `oidc.user:${process.env.REACT_APP_IDN_BASE_URL}:${clientId}`,
        JSON.stringify({
          access_token: loginUser.response.access_token,
          refresh_token: loginUser.response.refresh_token,
          profile: { sub: loginUser.response.user_id },
        })
      );
      localStorage.setItem("access_token", loginUser.response.access_token);
      localStorage.setItem("refresh_token", loginUser.response.refresh_token);
      localStorage.setItem("aspnetuser_id", loginUser.response.user_id);
      localStorage.setItem(
        "access_token_expiry",
        Date.now() + loginUser.response.expires_in * 1000
      );
      localStorage.setItem("user_id", email);
      localStorage.setItem("user_contactId", cid);
      localStorage.setItem("user_firstName", firstName);
      localStorage.setItem("user_lastName", lastName);
      localStorage.setItem("user_displayName", firstName + lastName);
      localStorage.setItem("user_primaryEmail", email);
      yield put(
        loginUserSuccess({
          uid: email,
        })
      );
      history.push("/app/home");
    } else {
      yield put(loginUserError(loginUser.message));
    }
  } catch (error) {
    yield put(loginUserError(error));
  }
}

const registerWithEmailPasswordAsync = async (email, password) => {
  if (email.length > 0 && password.length > 0)
    return {
      user: {
        uid: "101",
      },
      message: null,
    };
  return {
    user: null,
    message: "In-correct user name or password",
  };
};

function* registerWithEmailPassword({ payload }) {
  const { email, password } = payload.user;
  const { history } = payload;
  try {
    const registerUser = yield call(
      registerWithEmailPasswordAsync,
      email,
      password
    );
    if (!registerUser.message) {
      localStorage.setItem("user_id", registerUser.user.uid);
      yield put(registerUserSuccess(registerUser));
      history.push("/");
    } else {
      yield put(registerUserError(registerUser.message));
    }
  } catch (error) {
    yield put(registerUserError(error));
  }
}

const logoutAsync = async (history) => {
  if (history) {
    history.push("/logout");
  }
};

export const clearUserInfo = () => {
  localStorage.removeItem("user_id");
  localStorage.removeItem("user_contactId");
  localStorage.removeItem("user_firstName");
  localStorage.removeItem("user_lastName");
  localStorage.removeItem("user_displayName");
  localStorage.removeItem("user_primaryEmail");
  localStorage.removeItem("user_userAccountType");
};

export const clearDataFromSessionStorage = () => {
  const clientId = getClientId();
  sessionStorage.removeItem(
    `oidc.user:${process.env.REACT_APP_IDN_BASE_URL}:${clientId}`
  );
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("access_token_expiry");
  localStorage.removeItem("aspnetuser_id");
}

function* Logout({ payload }) {
  const { history } = payload;
  try {
    yield call(logoutAsync, history);
    clearUserInfo();
    clearDataFromSessionStorage();
  } catch (error) {
    console.log(error);
  }
}

const forgotPasswordAsync = async (email) => ({
  user: {
    uid: "101",
  },
  message: null,
  email,
});

function* forgotPassword({ payload }) {
  const { email } = payload.forgotUserMail;
  try {
    const forgotPasswordStatus = yield call(forgotPasswordAsync, email);
    if (!forgotPasswordStatus) {
      yield put(forgotPasswordSuccess("success"));
    } else {
      yield put(forgotPasswordError(forgotPasswordStatus.message));
    }
  } catch (error) {
    yield put(forgotPasswordError(error));
  }
}

const resetPasswordAsync = async (resetPasswordCode, newPassword) => ({
  user: {
    uid: "101",
  },
  message: null,
  resetPasswordCode,
  newPassword,
});

function* resetPassword({ payload }) {
  const { newPassword, resetPasswordCode } = payload;
  try {
    const resetPasswordStatus = yield call(
      resetPasswordAsync,
      resetPasswordCode,
      newPassword
    );
    if (!resetPasswordStatus) {
      yield put(resetPasswordSuccess("success"));
    } else {
      yield put(resetPasswordError(resetPasswordStatus.message));
    }
  } catch (error) {
    yield put(resetPasswordError(error));
  }
}

export function* watchLoginUser() {
  yield takeEvery(LOGIN_USER, loginWithEmailPassword);
}

export function* watchAutoLogin() {
  yield takeEvery(AUTO_LOGIN, AutoLogin);
}

export function* watchGetUserInfo() {
  yield takeEvery(GET_USER_INFO, getUserInfo);
}

export function* watchRegisterUser() {
  yield takeEvery(REGISTER_USER, registerWithEmailPassword);
}

export function* watchLogoutUser() {
  yield takeEvery(LOGOUT_USER, Logout);
}

export function* watchForgotPassword() {
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}

export function* watchResetPassword() {
  yield takeEvery(RESET_PASSWORD, resetPassword);
}

export default function* rootSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchAutoLogin),
    fork(watchLogoutUser),
    fork(watchRegisterUser),
    fork(watchForgotPassword),
    fork(watchResetPassword),
    fork(watchGetUserInfo),
  ]);
}
